/**
 * Sticky Cart Bar Custom Element - Optimized Working Version
 */
class StickyCartBar extends HTMLElement {
  constructor() {
    super();
    
    this.mainForm = null;
    this.stickyImage = null;
    this.stickyPrice = null;
    this.stickyComparePrice = null;
    this.stickyButton = null;
    this.stickyVariantInput = null;
    this.footerElement = null;
    this.intersectionObserver = null;
    this.currentVariantId = null;
    this.latestFormCondition = false;
    this.latestFooterCondition = false;
    
    this.debouncedHandleVariantChange = this.debounce(this.handleVariantChange.bind(this), 100);
    this.init();
    
    document.addEventListener('scripts:reloaded', () => {
      setTimeout(() => this.init(), 50);
    });
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

formatMoney(cents) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify
      .formatMoney(cents, window.theme?.moneyFormat || '${{amount}}')
      .replace(/\.00$/, '');
  }

  // Safe fallback (no .00)
  return '$' + (cents / 100).toString().replace(/\.00$/, '');
}


  updateStickyBar(variant) {
    if (!variant || this.currentVariantId === variant.id) return;
    this.currentVariantId = variant.id;
    
    // Update image
    if (variant.featured_image && this.stickyImage) {
      const imageUrl = variant.featured_image.src || variant.featured_image;
      this.stickyImage.src = imageUrl;
      this.stickyImage.srcset = imageUrl;
    }
    
    // Update variant ID input
    if (this.stickyVariantInput) {
      this.stickyVariantInput.value = variant.id;
    }
    
    // Update price
    if (this.stickyPrice && variant.price !== undefined) {
      this.stickyPrice.textContent = this.formatMoney(variant.price);
    }
    
    // Update compare price
    if (this.stickyComparePrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.stickyComparePrice.textContent = this.formatMoney(variant.compare_at_price);
        this.stickyComparePrice.style.display = 'inline';
      } else {
        this.stickyComparePrice.style.display = 'none';
      }
    }
    
    // Update button state
    if (this.stickyButton) {
      const buttonText = this.stickyButton.querySelector('span') || this.stickyButton;
      
      if (!variant.available) {
        this.stickyButton.disabled = true;
        this.stickyButton.classList.add('disabled', 'sold-out');
        buttonText.textContent = window.theme?.strings?.soldOut || 'Sold out';
      } else {
        this.stickyButton.disabled = false;
        this.stickyButton.classList.remove('disabled', 'sold-out');
        buttonText.textContent = window.theme?.strings?.addToCart || 'Add to cart';
      }
    }
  }

  getVariant(variantId) {
    let variant = window.product?.variants?.find(v => v.id == variantId);
    
    if (!variant && this.mainForm) {
      const productHandle = window.location.pathname.split('/products/')[1]?.split('/')[0];
      if (productHandle) {
        fetch(`/products/${productHandle}.js`)
          .then(response => response.json())
          .then(productData => {
            window.product = productData;
            const foundVariant = productData.variants.find(v => v.id == variantId);
            if (foundVariant) {
              this.updateStickyBar(foundVariant);
            }
          })
          .catch(() => console.warn('Could not fetch product data'));
      }
    }
    
    return variant;
  }

  handleVariantChange(variantId) {
    const variant = this.getVariant(variantId);
    if (variant) this.updateStickyBar(variant);
  }

  onVariantChange = (e) => {
    if (e.detail?.variant) this.updateStickyBar(e.detail.variant);
  }

  onFormChange = (e) => {
    if (e.target.name === 'id' || e.target.name.startsWith('options')) {
      setTimeout(() => {
        const variantInput = this.mainForm.querySelector('input[name="id"], select[name="id"]');
        if (variantInput?.value) this.debouncedHandleVariantChange(variantInput.value);
      }, 50);
    }
  }

  onVisibilityChange = (entries) => {
    const formEntry = entries.find(entry => entry.target === this.mainForm);
    const footerEntry = entries.find(entry => entry.target === this.footerElement);
    
    if (formEntry) {
      this.latestFormCondition = !formEntry.isIntersecting && formEntry.boundingClientRect.bottom < 0;
    }
    
    if (footerEntry) {
      this.latestFooterCondition = !footerEntry.isIntersecting;
    }
    
    this.classList.toggle('hide', !(this.latestFormCondition && this.latestFooterCondition));
  }

  init() {
    // Cleanup
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    if (this.mainForm) {
      this.mainForm.removeEventListener('variant:change', this.onVariantChange);
      this.mainForm.removeEventListener('change', this.onFormChange);
    }
    document.removeEventListener('variant:change', this.onVariantChange);
    
    // Find elements
    this.mainForm = document.querySelector('.product__info-wrapper product-form') ||
                   document.querySelector('product-form') ||
                   document.querySelector('[data-type="add-to-cart-form"]');
    
    if (!this.mainForm) {
      console.warn('Main form not found for sticky cart bar');
      return;
    }
    
    this.footerElement = document.querySelector('.footer') || 
                        document.querySelector('.shopify-section--footer') ||
                        document.querySelector('footer');
    
    if (!this.footerElement) {
      console.warn('Footer element not found for sticky cart bar');
      return;
    }
    
    // Get sticky bar elements
    this.stickyImage = this.querySelector('.bar__image img');
    this.stickyPrice = this.querySelector('.info__price p');
    this.stickyComparePrice = this.querySelector('.info__price s');
    this.stickyButton = this.querySelector('button[type="submit"], .product-form__cart-submit');
    this.stickyVariantInput = this.querySelector('input[name="id"]');
    
    // Reset state
    this.currentVariantId = null;
    this.latestFormCondition = false;
    this.latestFooterCondition = false;
    this.classList.add('hide');
    
    // Setup observers and listeners
    this.intersectionObserver = new IntersectionObserver(this.onVisibilityChange, { threshold: 0 });
    this.intersectionObserver.observe(this.mainForm);
    this.intersectionObserver.observe(this.footerElement);
    
    this.mainForm.addEventListener('variant:change', this.onVariantChange);
    document.addEventListener('variant:change', this.onVariantChange);
    this.mainForm.addEventListener('change', this.onFormChange);
  }
}

customElements.define('sticky-cart-bar', StickyCartBar);